import { useCallback, useEffect, useMemo, useState } from 'react';

import { getRouteApi, useNavigate, useParams } from '@tanstack/react-router';
import {
    APPLICANT_ATTRIBUTION_BOTH,
    APPLICANT_ATTRIBUTIONS,
    CUSTOMER_DATA_TABS,
    LOAN_REQUEST_COMMANDS,
    REAL_ESTATE_USAGES,
} from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { createId } from 'cpool-utils';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import ItemAddButton from '../General/ItemAddButton.tsx';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import {
    BUILDING_DESCRIPTION_STEP_PATHS,
    type BuildingDescriptionStepId,
    DEFAULT_BUILDING_DESCRIPTION_STEP_ID,
} from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/BuildingDescription/types';
import RealEstate from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/RealEstate.tsx';
import { CUSTOMER_DATA_TAB_PATHS } from '~/features/leadDetails/constants/tabPaths';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';
import { getBuildingDescriptionStepFromPath } from '~/features/leadDetails/utils/tabPaths';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const RealEstateFields: React.FC<Props> = ({ lead }) => {
    const hasSecondApplicant = !!lead.antragsteller2;
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control, getValues } = useFormContext<SchemaRealEstatesForm>();
    const realEstates = useWatch({ control, name: 'immobilien' });
    const navigate = useNavigate();
    const routeParams = useParams({ strict: false });
    const isRealEstateRoute = routeParams.customerTab === CUSTOMER_DATA_TAB_PATHS[CUSTOMER_DATA_TABS.REAL_ESTATE];
    const buildingDescriptionRoute = useMemo(
        () =>
            isRealEstateRoute && routeParams.realEstateId && routeParams.step
                ? {
                      realEstateId: routeParams.realEstateId,
                      step: getBuildingDescriptionStepFromPath(routeParams.step),
                  }
                : null,
        [isRealEstateRoute, routeParams.realEstateId, routeParams.step],
    );
    const [buildingDescriptionStepsByRealEstateId, setBuildingDescriptionStepsByRealEstateId] = useState<
        Record<string, BuildingDescriptionStepId>
    >({});
    const {
        setHasSelfOccupiedRealEstateApplicant1,
        setHasSelfOccupiedRealEstateApplicant2,
        setHasSelfOccupiedRealEstateShared,
    } = useLeadDetailsFormContext();
    const { append, fields, remove, update } = useFieldArray<SchemaRealEstatesForm, 'immobilien', 'realEstateId'>({
        control,
        keyName: 'realEstateId',
        name: 'immobilien',
    });

    const addRealEstate = () => {
        const entry: SchemaRealEstatesForm['immobilien'][number] = {
            baubeschreibung: null,
            baufinanzierungsrate: null,
            bezeichnung: null,
            darlehen: [],
            gehoertZuAntragsteller: APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1,
            id: createId(),
            immobilienart: null,
            mieteinnahmenKaltMonatlich: null,
            mieteinnahmenWarmMonatlich: null,
            nebenkostenMonatlich: null,
            nutzungsart: REAL_ESTATE_USAGES.VERMIETET,
            vermieteteWohnflaeche: null,
            wert: null,
            wohnflaeche: null,
        };
        append(entry);
    };

    const changeApplicantAttribution = (index: number, value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => {
        const realEstate = getValues(`immobilien.${index}`);
        update(index, { ...realEstate, gehoertZuAntragsteller: value });
    };

    const changeBuildingDescriptionStep = (realEstateId: string, step: BuildingDescriptionStepId) => {
        setBuildingDescriptionStepsByRealEstateId((current) => ({ ...current, [realEstateId]: step }));
        if (!isRealEstateRoute) return;
        navigate({
            params: {
                customerTab: CUSTOMER_DATA_TAB_PATHS[CUSTOMER_DATA_TABS.REAL_ESTATE],
                leadId,
                realEstateId,
                step: BUILDING_DESCRIPTION_STEP_PATHS[step],
            },
            search: (previous) => previous,
            to: '/leads/details/$leadId/customer-data/$customerTab/$realEstateId/$step',
        });
    };

    const leaveBuildingDescriptionStep = useCallback(() => {
        if (!isRealEstateRoute) return;
        navigate({
            params: { customerTab: CUSTOMER_DATA_TAB_PATHS[CUSTOMER_DATA_TABS.REAL_ESTATE], leadId },
            replace: true,
            search: (previous) => previous,
            to: '/leads/details/$leadId/customer-data/$customerTab',
        });
    }, [isRealEstateRoute, leadId, navigate]);

    const removeBuildingDescription = (realEstateId: string) => {
        setBuildingDescriptionStepsByRealEstateId((current) => ({
            ...current,
            [realEstateId]: DEFAULT_BUILDING_DESCRIPTION_STEP_ID,
        }));
        leaveBuildingDescriptionStep();
    };

    const getBuildingDescriptionStep = (realEstateId: string): BuildingDescriptionStepId => {
        if (buildingDescriptionRoute?.realEstateId === realEstateId) return buildingDescriptionRoute.step;
        return buildingDescriptionStepsByRealEstateId[realEstateId] ?? DEFAULT_BUILDING_DESCRIPTION_STEP_ID;
    };

    useEffect(() => {
        if (!buildingDescriptionRoute) return;

        const buildingDescriptionExists = realEstates.some(
            (realEstate) =>
                realEstate.id === buildingDescriptionRoute.realEstateId && realEstate.baubeschreibung !== null,
        );
        if (!buildingDescriptionExists) leaveBuildingDescriptionStep();
    }, [buildingDescriptionRoute, leaveBuildingDescriptionStep, realEstates]);

    const removeRealEstate = (index: number, applicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE) => {
        const type = getValues(`immobilien.${index}.nutzungsart`);
        // checks if the real estate is self occupied and sets the corresponding state to false
        if (type === REAL_ESTATE_USAGES.EIGENGENUTZT || type === REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET) {
            if (applicantAttribution === APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1)
                setHasSelfOccupiedRealEstateApplicant1(false);
            if (applicantAttribution === APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2)
                setHasSelfOccupiedRealEstateApplicant2(false);
            if (applicantAttribution === APPLICANT_ATTRIBUTION_BOTH.BEIDE) setHasSelfOccupiedRealEstateShared(false);
        }
        remove(index);
    };

    return (
        <div className="space-y-2">
            {fields.map((immobilie, realEstateIndex) => (
                <div
                    key={immobilie.realEstateId}
                    style={hide(
                        !!lead.coApplicantHidden &&
                            immobilie.gehoertZuAntragsteller === APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2,
                    )}
                >
                    <RealEstate
                        buildingDescriptionStep={getBuildingDescriptionStep(immobilie.id)}
                        currentApplicantAttribution={immobilie.gehoertZuAntragsteller}
                        translationPath={'REAL_ESTATE.LABELS.HEADER'}
                        removeRealEstate={removeRealEstate}
                        realEstateCount={realEstateIndex + 1}
                        realEstateIndex={realEstateIndex}
                        onApplicantAttributionChange={changeApplicantAttribution}
                        onBuildingDescriptionRemove={() => removeBuildingDescription(immobilie.id)}
                        onBuildingDescriptionStepChange={(step) => changeBuildingDescriptionStep(immobilie.id, step)}
                        hasSecondApplicant={hasSecondApplicant}
                    />
                </div>
            ))}
            <div>
                <ItemAddButton
                    onClick={addRealEstate}
                    translationPath="REAL_ESTATE.BUTTONS.ADD_REAL_ESTATE"
                    testId="real-estate-add-icon"
                    disabled={
                        !canExecuteForAttribution({
                            attribution: APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1,
                            commandName: LOAN_REQUEST_COMMANDS.changeRealEstate,
                            permissions,
                        })
                    }
                />
            </div>
        </div>
    );
};

export default RealEstateFields;
